import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../../../styles/home.desktop.scss';
import '../../../styles/home.mobile.scss';
import en from '../../../locales/en.json';
import mk from '../../../locales/mk.json';
import al from '../../../locales/al.json';
import HeroSection from './sections/hero.section.view';
import AboutSection from './sections/about.section.view';
import EventDetailsSection from './sections/event-details.section.view';
import RegistrationSection from './sections/registration.section.view';
import VolunteerSection from './sections/volunteer.section.view';
import SponsorsSection from './sections/sponsors.section.view';
import NewsSection from './sections/news.section.view';
import ContactSection from './sections/contact.section.view';
import PageTemplate, { Lang } from '../../../components/layout/PageTemplate';
import api from '../../../utils/apiservice';
import RegistrationOverSection from './sections/registration-over.section.view';

type Translation = typeof en & { readPrivacyPolicy: string };

const localeMap = {
  en: en as Translation,
  mk: { ...(mk as object), readPrivacyPolicy: (mk as any).readPrivacyPolicy || '' } as Translation,
  al: { ...(al as object), readPrivacyPolicy: (al as any).readPrivacyPolicy || '' } as Translation,
};

function getInitialLang(): Lang {
  // Check localStorage, fallback to mk
  const stored = localStorage.getItem('lang');
  if (stored === 'en' || stored === 'mk' || stored === 'al') return stored;
  return 'mk';
}

interface RaceData {
  id: string;
  name: string;
  startDate: string;
  openRegistrationDate?: string | null;
  closeRegistrationDate?: string | null;
  status?: string;
}

export const HomeView = (props: any) => {
  const [lang, setLang] = useState<Lang>(getInitialLang());
  const t = localeMap[lang];
  const location = useLocation();
  const [race, setRace] = useState<RaceData | null>(null);

  // Fetch race from the API — use the configured organizer to pick the correct race
  useEffect(() => {
    const fetchRace = async () => {
      try {
        // Get the configured organizerId from the backend
        const configRes = await api.get('/api/config/public');
        const organizerId = configRes.data?.organizerId;
        const params: Record<string, string> = {};
        if (organizerId) params.organizerId = organizerId;

        const res = await api.get('/api/races', { params });
        const races: RaceData[] = res.data;
        if (races.length > 0) {
          // Pick the latest active (non-completed/cancelled) race from this organizer
          const activeStatuses = ['upcoming', 'active', 'open'];
          const active = races.filter(r => !r.status || activeStatuses.includes(r.status.toLowerCase()));
          const pool = active.length > 0 ? active : races;
          const sorted = [...pool].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
          setRace(sorted[0]);
        }
      } catch (err) {
        console.error('Failed to fetch race data:', err);
      }
    };
    fetchRace();
  }, []);

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.home-header');
      const hero = document.querySelector('.hero-section');
      if (!header || !hero) return;
      const heroHeight = (hero as HTMLElement).offsetHeight;
      if (window.scrollY > heroHeight - 80) {
        header.classList.add('scrolled');
        header.classList.add('sticky');
      } else {
        header.classList.remove('scrolled');
        header.classList.remove('sticky');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section if hash is present in URL (for navigation from other pages or clicking section links)
  // Scroll to section if hash is present in URL (for navigation from other pages or clicking section links)
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          const yOffset = -80;
          const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 200); // Wait for page to render
    }
  }, [location]);

  // Smooth scroll for Register Now button
  const handleRegisterClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const reg = document.getElementById('register');
    if (reg) {
      reg.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ...existing code...

  // Countdown timer logic — driven by race.startDate from API
  const [timer, setTimer] = useState<string>('');
  useEffect(() => {
    const raceStartDate = race?.startDate ? new Date(race.startDate) : new Date(2025, 8, 21, 9, 0, 0, 0);
    const updateTimer = () => {
      const now = new Date();
      let diff = raceStartDate.getTime() - now.getTime();
      if (diff < 0) diff = 0;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setTimer(
        `${days.toString().padStart(2, '0')} : ` +
        `${hours.toString().padStart(2, '0')} : ` +
        `${minutes.toString().padStart(2, '0')} : ` +
        `${seconds.toString().padStart(2, '0')}`
      );
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [race]);

  // Helper: get timer date string for About section
  const raceStartDate = race?.startDate ? new Date(race.startDate) : new Date(2025, 8, 21, 9, 0, 0, 0);
  const localeMapForDate = { en: 'en-US', mk: 'mk-MK', al: 'sq-AL' };
  const timerDateString = raceStartDate.toLocaleDateString(localeMapForDate[lang], {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
  }) + (lang === 'en'
    ? ` ${raceStartDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
    : ` ${raceStartDate.toLocaleTimeString('mk-MK', { hour: '2-digit', minute: '2-digit' })}`);

  // Determine if registration is currently open based on race dates
  const now = new Date();
  const isRegistrationOpen = race?.openRegistrationDate && race?.closeRegistrationDate
    ? now >= new Date(race.openRegistrationDate) && now <= new Date(race.closeRegistrationDate)
    : false;

  // Scroll to top handler for the floating button
  const handleScrollTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // cPay payment handler for demo/test button
  const handleCpayPay = async (paymentData: any) => {
    // Add all required fields for demo
    const fullPaymentData = {
      ...paymentData,
      PayToMerchant: "1000002503",
      MerchantName: "DRIM AKTIV",
      AmountCurrency: "MKD",
      PaymentOKURL: "https://www.drimrun.mk/success-registration",
      PaymentFailURL: "https://www.drimrun.mk/fail-registration",
      FirstName: "Test",
      LastName: "User",
      Address: "Test Address",
      City: "Skopje",
      Zip: "1000",
      Country: "North Macedonia",
      Telephone: "+389 70 123 456",
      Email: "test@drimrun.mk",
      CheckSumFields: [
        "AmountToPay","PayToMerchant","MerchantName","AmountCurrency","Details1","Details2","PaymentOKURL","PaymentFailURL",
        "FirstName","LastName"
      ]
    };
    // Call backend to get checksum and header using api service
    const res = await api.post('/payment/cpay-checksum', fullPaymentData, {
      headers: { 'Content-Type': 'application/json' }
    });
    const { checksum, checkSumHeader } = res.data;
    // Create and submit the form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://vpos.cpay.com.mk/mk-MK';
    form.innerHTML = `
      <input name="isSimple" value="true" type="hidden" />
      <input name="AmountToPay" value="${fullPaymentData.amount}" type="hidden" />
      <input name="PayToMerchant" value="${fullPaymentData.PayToMerchant}" type="hidden" />
      <input name="MerchantName" value="${fullPaymentData.MerchantName}" type="hidden" />
      <input name="AmountCurrency" value="${fullPaymentData.AmountCurrency}" type="hidden" />
      <input name="Details1" value="${fullPaymentData.Details1}" type="hidden" />
      <input name="Details2" value="${fullPaymentData.Details2}" type="hidden" />
      <input name="PaymentOKURL" value="${fullPaymentData.PaymentOKURL}" type="hidden" />
      <input name="PaymentFailURL" value="${fullPaymentData.PaymentFailURL}" type="hidden" />
      <input name="FirstName" value="${fullPaymentData.FirstName}" type="hidden" />
      <input name="LastName" value="${fullPaymentData.LastName}" type="hidden" />
      <input name="CheckSumHeader" value="${checkSumHeader}" type="hidden" />
      <input name="CheckSum" value="${checksum}" type="hidden" />
    `;
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <PageTemplate lang={lang} setLang={setLang} t={t}>
      <HeroSection t={t} timer={timer} handleRegisterClick={handleRegisterClick} race={race} />
      <AboutSection t={t} timerDateString={timerDateString} />
      <EventDetailsSection t={t} />
      {isRegistrationOpen
        ? <RegistrationSection t={t} props={props} />
        : <RegistrationOverSection t={t} props={props} />
      }
      <SponsorsSection t={t} raceId={race?.id} />
      <VolunteerSection t={t} />
      {/* <NewsSection t={t} /> */}
      <ContactSection t={t} />
      {/* The floating scroll-to-top button can also be included here if needed */}
      <a
        href="#top"
        className="btn scroll-top-btn"
        onClick={handleScrollTop}
        style={{
          position: 'fixed',
          right: 32,
          bottom: 48,
          zIndex: 200,
          background: '#1e6ad6',
          color: '#fff',
          borderRadius: 32,
          padding: '12px 24px',
          fontWeight: 700,
          fontSize: '1.1rem',
          boxShadow: '0 4px 24px rgba(30,106,214,0.15)',
          border: 'none',
          textDecoration: 'none',
          letterSpacing: 1,
          transition: 'background 0.2s, color 0.2s',
        }}
      >
        ↑ Top
      </a>
    </PageTemplate>
  );
};

export default HomeView;
